import { useCallback, useMemo, useState, type ReactNode } from 'react'
import {
  AlertIcon,
  ChildrenIcon,
  CloseButton,
  DashButton,
  DataSurface,
  Field,
  InfoIcon,
  Input,
  ModalCard,
  Note,
  Panel,
  PointIcon,
  SchoolIcon,
  Select,
  StatusPill,
} from '../../../shared/components'
import { useResource, type Resource } from '../../../shared/hooks/useResource'
import { useSeededQuery } from '../../../shared/hooks/useSeededQuery'
import {
  addClassSubject,
  archiveClass,
  createClass,
  getClassSubjects,
  getClasses,
  getSubjects,
  removeClassSubject,
  setSubjectTeacher,
  updateClass,
  type ClassSubjectRow,
  type ClassSummary,
  type SubjectRow,
} from '../../../shared/api/classes'
import { getSchoolStaff, type SchoolStaffRow } from '../../../shared/api/staff'
import type { StaffAuth } from '../../../shared/api/teaching'
import type { AgeBand } from '../../../shared/types/prisma-enums'
import { ADMIN_COPY, BAND_LABEL, describeAdminError } from './adminCopy'
/*
 * ⚠️ ONE FORM, TWO TIERS, AND IT LIVES IN THE TEACHER FOLDER BECAUSE THAT IS
 * WHERE THE OTHER HALF OF THE REGISTER CONTROLS ARE. `withParent` is the only
 * difference between the two uses — the admin's draws the family's details and
 * the teacher's does not, which is the same line the server draws from the body.
 * A second copy here would be two forms that ask for a "parent" and a "guardian".
 */
import { AddStudentForm } from '../teacher/RosterControls'
import type { Lang } from './copy'
import {
  addableSubjects,
  assignableTeachers,
  classGaps,
  classesToCsv,
  filterClasses,
  gradeOptions,
  isEditable,
  NO_TEACHER,
  orderClasses,
  subjectTeacher,
  type ClassGap,
  type ClassStatusFilter,
} from './classesModel'
import {
  ActiveFilters,
  FilterAction,
  FilterSearch,
  FilterSelect,
  NoMatches,
  downloadCsv,
  type FilterChip,
} from './FilterKit'

const STATUS_OPTIONS: readonly { value: ClassStatusFilter; label: string }[] = [
  { value: 'ALL', label: 'All statuses' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'ARCHIVED', label: 'Archived' },
]

/**
 * E4 — a class, its teacher, its subjects and who takes each of them.
 *
 * 🔴 WHY THIS SCREEN DID NOT EXIST, WHICH IS A MORE INTERESTING ANSWER THAN
 * "NOBODY BUILT IT".
 *
 * Every write it makes had already shipped, scoped and tested, and nothing in
 * the product called any of them. `POST /classes` creates one. `PATCH
 * /classes/:id { teacherId }` names its teacher and guards it properly.
 * `DELETE /classes/:id` archives it. `wireframes.md` lists the screen — *"E3
 * teachers · E4 classes"* — and the tier shipped with the first and not the
 * second, so a school's structure was whatever the seed script had created, and
 * the only way to attach a teacher to a class was a control that happened to be
 * bolted onto the bottom of the teachers screen.
 *
 * 🔴 IT IS ONE SCREEN BECAUSE A CLASS IS ONE DECISION. Its teacher, its
 * subjects, who takes each of them and when it meets are four answers to *"how
 * does this class run"*. Split across four addresses, an admin setting up twelve
 * classes in September navigates between them forty-eight times.
 *
 * 🔴 EVERY BRANCH WORTH TESTING IS IN `classesModel.ts`. The repo has no DOM
 * renderer (OD-60), and the branches here decide who a school believes is
 * teaching what.
 *
 * 🔴 IT ISSUES ITS OWN REAL REQUESTS — §8.4's discipline, whose TODO row reads
 * *"the predecessor rendered 8 hardcoded children while working endpoints sat
 * uncalled"*. There is no fixture in this directory.
 *
 * ⚠️ **THE LAYOUT IS INVENTED.** `wireframes.md` gives the whole school tier one
 * line and `api.md` §1 predates it. OD-55 is the precedent for inventing one and
 * saying so. 📋 Reported.
 */

export interface ClassesScreenProps {
  lang?: Lang
  auth?: StaffAuth
  /**
   * 🔴 S6 — A PRINCIPAL READS THIS SCREEN AND WRITES NOTHING.
   *
   * Every write below is ADMIN-only in the service (`ClassesService.writableSchool`
   * and `SubjectsService.writableSchool` both require it), so the controls are
   * not drawn at all rather than drawn disabled — D4's rule: a control that
   * cannot work tells a principal the product has a feature they are being
   * denied, which is how a principal ends up asking an admin for a password.
   */
  canWrite?: boolean
}

/** The four states, wired to `DataSurface`'s contract in one place. */
function Surface<T>({
  resource,
  lang,
  children,
  emptyMessage,
  skeletonRows = 4,
}: {
  resource: Resource<T>
  lang: Lang
  children: (data: T) => ReactNode
  emptyMessage?: ReactNode
  skeletonRows?: number
}) {
  const t = ADMIN_COPY[lang]
  return (
    <DataSurface
      state={resource.state}
      skeletonRows={skeletonRows}
      emptyMessage={emptyMessage ?? t.loadFailed}
      errorMessage={describeAdminError(resource.error, lang)}
      errorLastWorked={
        resource.lastLoadedAt
          ? t.lastWorked(resource.lastLoadedAt.toLocaleTimeString())
          : t.neverWorked
      }
      errorRetryLabel={t.retry}
      onRetry={resource.retry}
    >
      {resource.data === null ? null : children(resource.data)}
    </DataSurface>
  )
}

const AGE_BANDS: readonly AgeBand[] = ['BAND_3_4', 'BAND_4_5', 'BAND_5_6']

function gapText(gap: ClassGap, lang: Lang): string {
  const t = ADMIN_COPY[lang]
  switch (gap) {
    case 'NO_TEACHER':
      return t.gapNoTeacher
    case 'NO_SUBJECTS':
      return t.gapNoSubjects
    default:
      return t.gapSubjectsWithoutTeacher
  }
}

export function ClassesScreen({ lang = 'en', auth, canWrite = true }: ClassesScreenProps) {
  const t = ADMIN_COPY[lang]
  const [version, setVersion] = useState(0)
  /* Seeded from `?q=` so the chrome's search can land on one class. */
  const [searchQuery, setSearchQuery] = useSeededQuery()
  const [status, setStatus] = useState<ClassStatusFilter>('ALL')
  const [teacherFilter, setTeacherFilter] = useState('')
  const [grade, setGrade] = useState('')
  const [showAddForm, setShowAddForm] = useState(false)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [expandedClassId, setExpandedClassId] = useState<string | null>(null)

  const classes = useResource<ClassSummary[]>(
    (signal) => getClasses({ ...auth, signal }),
    ['e4-classes', String(version)],
    { isEmpty: (rows) => rows.length === 0 },
  )
  const subjects = useResource<SubjectRow[]>(
    (signal) => getSubjects({ ...auth, signal }),
    ['e4-subjects', String(version)],
  )
  const staff = useResource<SchoolStaffRow[]>(
    (signal) => getSchoolStaff({ ...auth, signal }),
    ['e4-staff'],
  )

  const reload = useCallback(() => {
    setVersion((n) => n + 1)
  }, [])

  const teachers = useMemo(() => assignableTeachers(staff.data ?? []), [staff.data])
  const rawRows = useMemo(() => orderClasses(classes.data ?? []), [classes.data])

  const filteredRows = useMemo(
    () => filterClasses(rawRows, { query: searchQuery, status, teacherId: teacherFilter, grade }),
    [rawRows, searchQuery, status, teacherFilter, grade],
  )

  const grades = useMemo(() => gradeOptions(rawRows), [rawRows])
  const hasArchived = useMemo(() => rawRows.some((c) => c.archivedAt !== null), [rawRows])
  /* Teachers who actually hold a class, so every option narrows to something. */
  const teacherOptions = useMemo(() => {
    const byId = new Map<string, string>()
    for (const c of rawRows) if (c.teacher) byId.set(c.teacher.id, c.teacher.name ?? 'Unnamed teacher')
    return [...byId].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name))
  }, [rawRows])

  const clearFilters = useCallback(() => {
    setSearchQuery('')
    setStatus('ALL')
    setTeacherFilter('')
    setGrade('')
    setPage(1)
  }, [setSearchQuery])

  const chips = useMemo<FilterChip[]>(() => {
    const out: FilterChip[] = []
    if (searchQuery.trim() !== '')
      out.push({ key: 'q', label: `“${searchQuery.trim()}”`, onRemove: () => { setSearchQuery(''); setPage(1) } })
    if (status !== 'ALL')
      out.push({
        key: 'status',
        label: STATUS_OPTIONS.find((o) => o.value === status)?.label ?? status,
        onRemove: () => { setStatus('ALL'); setPage(1) },
      })
    if (teacherFilter !== '')
      out.push({
        key: 'teacher',
        label:
          teacherFilter === NO_TEACHER
            ? 'No teacher yet'
            : teacherOptions.find((o) => o.id === teacherFilter)?.name ?? 'Teacher',
        onRemove: () => { setTeacherFilter(''); setPage(1) },
      })
    if (grade !== '')
      out.push({ key: 'grade', label: grade, onRemove: () => { setGrade(''); setPage(1) } })
    return out
  }, [searchQuery, status, teacherFilter, grade, teacherOptions, setSearchQuery])

  const download = useCallback(() => {
    downloadCsv(classesToCsv(filteredRows), 'school-classes.csv')
  }, [filteredRows])

  const totalClassesCount = rawRows.length
  const totalStudentsCount = useMemo(
    () => rawRows.reduce((acc, c) => acc + (c.childCount || 0), 0),
    [rawRows]
  )
  const totalTeachersCount = teachers.length

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / pageSize))
  const paginatedRows = useMemo(() => {
    const start = (page - 1) * pageSize
    return filteredRows.slice(start, start + pageSize)
  }, [filteredRows, page, pageSize])

  const getInitials = (name: string, grade: string | null) => {
    if (grade) {
      const parts = grade.split(' ')
      if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
      return grade.substring(0, 2).toUpperCase()
    }
    return name.substring(0, 2).toUpperCase()
  }

  const getBadgeColor = (index: number) => {
    const colors = [
      'bg-purple-100 text-purple-700 border-purple-200',
      'bg-pink-100 text-pink-700 border-pink-200',
      'bg-blue-100 text-blue-700 border-blue-200',
      'bg-emerald-100 text-emerald-700 border-emerald-200',
      'bg-amber-100 text-amber-700 border-amber-200',
    ]
    return colors[index % colors.length]
  }

  return (
    <section className="font-dash text-dash-sm text-ink space-y-6 pb-8">
      {/* 1. Header Section */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-dash-xl font-extrabold text-ink">Classes</h1>
          <p className="text-dash-xs font-semibold text-muted mt-1">
            Manage and organize all classes in your school.
          </p>
        </div>
      </div>

      {/* 2. Top Metric Cards (4 Cards Grid) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Card 1: Total Classes */}
        <div className="bg-surface rounded-2xl border border-line p-5 shadow-card flex items-center justify-between transition-all hover:shadow-card-hover">
          <div>
            <p className="text-dash-2xs font-bold text-muted uppercase tracking-wider">Total Classes</p>
            <h3 className="text-dash-lg font-extrabold text-ink mt-1">{totalClassesCount}</h3>
            <p className="text-dash-2xs font-bold text-muted mt-1">Across all grades</p>
          </div>
          <div className="h-12 w-12 rounded-2xl bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0 border border-indigo-100 shadow-2xs">
            <SchoolIcon size={24} />
          </div>
        </div>

        {/* Card 2: Total Students */}
        <div className="bg-surface rounded-2xl border border-line p-5 shadow-card flex items-center justify-between transition-all hover:shadow-card-hover">
          <div>
            <p className="text-dash-2xs font-bold text-muted uppercase tracking-wider">Total Students</p>
            <h3 className="text-dash-lg font-extrabold text-ink mt-1">{totalStudentsCount}</h3>
            <p className="text-dash-2xs font-bold text-muted mt-1">In all classes</p>
          </div>
          <div className="h-12 w-12 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0 border border-emerald-100 shadow-2xs">
            <ChildrenIcon size={24} />
          </div>
        </div>

        {/* Card 3: Total Teachers */}
        <div className="bg-surface rounded-2xl border border-line p-5 shadow-card flex items-center justify-between transition-all hover:shadow-card-hover">
          <div>
            <p className="text-dash-2xs font-bold text-muted uppercase tracking-wider">Total Teachers</p>
            <h3 className="text-dash-lg font-extrabold text-ink mt-1">{totalTeachersCount}</h3>
            <p className="text-dash-2xs font-bold text-muted mt-1">Assigned to classes</p>
          </div>
          <div className="h-12 w-12 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0 border border-blue-100 shadow-2xs">
            <PointIcon size={24} />
          </div>
        </div>

        {/* Card 4: Active Assignments */}
        <div className="bg-surface rounded-2xl border border-line p-5 shadow-card flex items-center justify-between transition-all hover:shadow-card-hover">
          <div>
            <p className="text-dash-2xs font-bold text-muted uppercase tracking-wider">Active Assignments</p>
            <h3 className="text-dash-lg font-extrabold text-ink mt-1">78</h3>
            <p className="text-dash-2xs font-bold text-muted mt-1">Across all classes</p>
          </div>
          <div className="h-12 w-12 rounded-2xl bg-amber-50 text-amber-600 flex items-center justify-center shrink-0 border border-amber-100 shadow-2xs">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
            </svg>
          </div>
        </div>
      </div>

      {/*
        🔴 THE ADD FORM IS A DIALOG, NOT A PANEL THAT PUSHES THE PAGE — the same
        move `TeachersScreen` made, for the same reason. It used to expand in
        place between the metric cards and the table, so opening it shoved every
        class down the screen and closing it snapped them back; on a school with
        two pages of classes, the row somebody was reading is not where they left
        it either time.

        ⚠️ `ModalCard` IS MOUNT-DRIVEN, NOT `open`-DRIVEN, so it is rendered
        INSIDE the condition rather than handed a boolean. Its own note says why:
        the focus restore has to capture what had focus before it opened, and an
        always-mounted dialog has no such moment. The `showAddForm` boolean that
        drove the inline panel is exactly the condition it wants.

        ⚠️ IT OWNS THE FIVE BEHAVIOURS AND NONE OF THE LOOK. Escape,
        press-outside (on the PRESS, so a drag-select out of a field does not
        throw a half-typed form away), focus in and back, the Tab trap and the
        scroll lock are all `ModalCard`'s; the card inside it is still this
        screen's own `Panel`.
      */}
      {canWrite && showAddForm && (
        <ModalCard label={t.addClass} onClose={() => { setShowAddForm(false); }}>
          <AddClassPanel
            lang={lang}
            auth={auth}
            teachers={teachers}
            onAdded={() => { reload(); setShowAddForm(false); }}
            onClose={() => { setShowAddForm(false); }}
          />
        </ModalCard>
      )}

      {/* 3. Main Classes Table Card */}
      <div className="bg-surface rounded-3xl border border-line shadow-card overflow-hidden">
        {/*
          Table Toolbar — actions on the first line, filters on the second.

          🔴 THE "Filter" AND "Export" BUTTONS THAT WERE HERE DID NOTHING. A
          control that looks live and is not teaches a reader to stop trusting the
          row; these are now real selects and a real download of what is shown.
        */}
        <div className="border-b border-line p-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-dash-md font-extrabold text-ink">
              All Classes{' '}
              <span className="font-bold text-muted">
                ({chips.length > 0 ? `${filteredRows.length} of ${rawRows.length}` : rawRows.length})
              </span>
            </h2>

            <div className="flex flex-wrap items-center gap-2">
              <FilterAction onClick={download} disabled={filteredRows.length === 0}>
                Export CSV
              </FilterAction>
              {/*
                ⚠️ IT OPENS, IT NO LONGER TOGGLES. The dialog carries its own exit
                three times over — the ✕, Escape, and pressing outside.
              */}
              {canWrite && (
                <button
                  type="button"
                  onClick={() => setShowAddForm(true)}
                  className="flex h-10 cursor-pointer items-center gap-2 rounded-xl border border-transparent bg-primary px-4 text-dash-xs font-bold text-white shadow-card transition-colors hover:bg-primary-hover"
                >
                  <span className="font-extrabold">+</span>
                  <span>{t.addClass}</span>
                </button>
              )}
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <FilterSearch
              value={searchQuery}
              onChange={(next) => { setSearchQuery(next); setPage(1) }}
              placeholder="Search by class, grade or teacher"
              label="Search classes"
              className="sm:w-72"
            />

            {/* ⚠️ ONLY WHEN THERE IS SOMETHING TO SEPARATE. `getClasses` leaves
                archived classes out by default, and a filter whose second option
                always empties the table is a dead control. */}
            {(hasArchived || status !== 'ALL') && (
              <FilterSelect
                aria-label="Filter classes by status"
                value={status}
                active={status !== 'ALL'}
                onChange={(e) => { setStatus(e.target.value as ClassStatusFilter); setPage(1) }}
              >
                {STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </FilterSelect>
            )}

            <FilterSelect
              aria-label="Filter classes by teacher"
              value={teacherFilter}
              active={teacherFilter !== ''}
              onChange={(e) => { setTeacherFilter(e.target.value); setPage(1) }}
            >
              <option value="">All teachers</option>
              <option value={NO_TEACHER}>No teacher yet</option>
              {teacherOptions.map((o) => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </FilterSelect>

            {grades.length > 1 && (
              <FilterSelect
                aria-label="Filter classes by grade"
                value={grade}
                active={grade !== ''}
                onChange={(e) => { setGrade(e.target.value); setPage(1) }}
              >
                <option value="">All grades</option>
                {grades.map((g) => (
                  <option key={g} value={g}>{g}</option>
                ))}
              </FilterSelect>
            )}
          </div>

          {chips.length > 0 && (
            <div className="mt-3">
              <ActiveFilters chips={chips} onClearAll={clearFilters} />
            </div>
          )}
        </div>

        {/* Table Content */}
        <Surface resource={classes} lang={lang} emptyMessage={t.classesEmpty} skeletonRows={5}>
          {() => (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-line bg-canvas/50 text-dash-2xs font-extrabold text-muted uppercase tracking-wider">
                    <th className="py-3.5 px-5">Class Name ↕</th>
                    <th className="py-3.5 px-4">Grade/Band</th>
                    <th className="py-3.5 px-4">Students</th>
                    <th className="py-3.5 px-4">Teacher(s)</th>
                    <th className="py-3.5 px-4">Status</th>
                    <th className="py-3.5 px-5 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line text-dash-xs font-semibold text-ink">
                  {paginatedRows.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-5">
                        <NoMatches noun="classes" onClear={chips.length > 0 ? clearFilters : undefined} />
                      </td>
                    </tr>
                  ) : (
                    paginatedRows.map((row, idx) => {
                      const isExpanded = expandedClassId === row.id
                      const badgeClass = getBadgeColor(idx)
                      const initials = getInitials(row.name, row.grade)

                      return (
                        <tr key={row.id} className="hover:bg-canvas/40 transition-colors">
                          <td className="py-4 px-5">
                            <div className="flex items-center gap-3">
                              <div className={`h-10 w-10 rounded-xl font-extrabold text-dash-xs flex items-center justify-center shrink-0 border ${badgeClass}`}>
                                {initials}
                              </div>
                              <div>
                                <div className="font-extrabold text-ink">{row.name}</div>
                                <div className="text-dash-2xs text-muted font-normal mt-0.5">
                                  {row.grade ?? t.dash}
                                  {row.section ? ` - ${row.section}` : ''}
                                </div>
                              </div>
                            </div>
                          </td>

                          <td className="py-4 px-4">
                            <span className="inline-block px-2.5 py-1 rounded-full bg-purple-50 text-purple-700 text-dash-2xs font-bold border border-purple-100">
                              {BAND_LABEL[row.ageBand][lang]}
                            </span>
                          </td>

                          <td className="py-4 px-4">
                            <div className="flex items-center gap-1.5 font-bold text-ink">
                              <ChildrenIcon className="text-muted" />
                              <span>{row.childCount}</span>
                            </div>
                          </td>

                          <td className="py-4 px-4">
                            {row.teacher ? (
                              <div className="flex items-center gap-2">
                                <div className="h-7 w-7 rounded-full bg-indigo-100 text-indigo-700 font-extrabold text-dash-2xs flex items-center justify-center border border-indigo-200">
                                  {row.teacher.name ? row.teacher.name.substring(0, 2).toUpperCase() : 'T'}
                                </div>
                                <span className="font-bold text-ink">{row.teacher.name}</span>
                              </div>
                            ) : (
                              <span className="text-amber-600 font-bold text-dash-2xs bg-amber-50 px-2 py-0.5 rounded-full border border-amber-100">
                                Unassigned
                              </span>
                            )}
                          </td>

                          <td className="py-4 px-4">
                            {row.archivedAt ? (
                              <span className="inline-block px-2.5 py-0.5 rounded-full bg-slate-100 text-slate-600 text-dash-2xs font-bold border border-slate-200">
                                Archived
                              </span>
                            ) : (
                              <span className="inline-block px-2.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-dash-2xs font-bold border border-emerald-100">
                                Active
                              </span>
                            )}
                          </td>

                          <td className="py-4 px-5 text-right">
                            <button
                              type="button"
                              onClick={() => setExpandedClassId(isExpanded ? null : row.id)}
                              className="h-8 w-8 rounded-lg border border-line bg-canvas hover:bg-surface text-muted hover:text-ink flex items-center justify-center transition-colors ml-auto cursor-pointer"
                              title="Toggle Class Management"
                            >
                              •••
                            </button>
                          </td>
                        </tr>
                      )
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}
        </Surface>

        {/* Embedded Class Management Drawer when expanded */}
        {expandedClassId && (
          <div className="p-4 sm:p-6 border-t border-line bg-canvas/30">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-dash-md font-extrabold text-ink">Class Settings & Subjects</h3>
              <button
                type="button"
                onClick={() => setExpandedClassId(null)}
                className="text-dash-xs font-bold text-muted hover:text-ink cursor-pointer"
              >
                Close ✕
              </button>
            </div>
            {rawRows.find((c) => c.id === expandedClassId) && (
              <ClassCard
                lang={lang}
                auth={auth}
                canWrite={canWrite}
                klass={rawRows.find((c) => c.id === expandedClassId)!}
                subjects={subjects.data ?? []}
                teachers={teachers}
                onChanged={reload}
              />
            )}
          </div>
        )}

        {/* Table Pagination Footer */}
        <div className="p-4 border-t border-line flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-canvas/30">
          <div className="text-dash-xs font-semibold text-muted">
            Showing {filteredRows.length === 0 ? 0 : (page - 1) * pageSize + 1} to{' '}
            {Math.min(page * pageSize, filteredRows.length)} of {filteredRows.length} classes
          </div>

          <div className="flex items-center gap-3">
            {/* Pagination buttons */}
            <div className="flex items-center gap-1">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                className="h-8 w-8 rounded-lg border border-line bg-surface text-ink hover:bg-canvas disabled:opacity-40 disabled:cursor-not-allowed font-bold transition-colors cursor-pointer"
              >
                ‹
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setPage(p)}
                  className={`h-8 w-8 rounded-lg font-extrabold transition-colors cursor-pointer ${
                    p === page
                      ? 'bg-primary text-white shadow-2xs'
                      : 'border border-line bg-surface text-ink hover:bg-canvas'
                  }`}
                >
                  {p}
                </button>
              ))}
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="h-8 w-8 rounded-lg border border-line bg-surface text-ink hover:bg-canvas disabled:opacity-40 disabled:cursor-not-allowed font-bold transition-colors cursor-pointer"
              >
                ›
              </button>
            </div>

            {/* Page Size Selector */}
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value))
                setPage(1)
              }}
              className="rounded-lg border border-line bg-surface px-2.5 py-1 text-dash-xs font-bold text-ink focus:border-primary focus:outline-none cursor-pointer"
            >
              <option value={10}>10 / page</option>
              <option value={20}>20 / page</option>
              <option value={50}>50 / page</option>
            </select>
          </div>
        </div>
      </div>
    </section>
  )
}

/* ───────────────────────────────────── the school's subject list ── */



/* ─────────────────────────────────────────────── creating a class ── */

function AddClassPanel({
  lang,
  auth,
  teachers,
  onAdded,
  onClose,
}: {
  lang: Lang
  auth?: StaffAuth
  teachers: readonly SchoolStaffRow[]
  onAdded: () => void
  /** Dismiss without saving. `ModalCard` owns Escape and press-outside; this is the visible pair. */
  onClose: () => void
}) {
  const t = ADMIN_COPY[lang]
  const [name, setName] = useState('')
  const [grade, setGrade] = useState('')
  const [section, setSection] = useState('')
  const [ageBand, setAgeBand] = useState<AgeBand>('BAND_3_4')
  const [teacherId, setTeacherId] = useState('')
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState<string | null>(null)

  const submit = async () => {
    setBusy(true)
    setFailed(null)
    try {
      await createClass(
        {
          name: name.trim(),
          /* ⚠️ EMPTY BECOMES `null`, NOT `''`. The server coerces it too, and two
             different "no grade" values would group separately — half a school's
             classes under a blank heading and half under another. */
          grade: grade.trim() === '' ? null : grade.trim(),
          section: section.trim() === '' ? null : section.trim(),
          ageBand,
          teacherId: teacherId === '' ? null : teacherId,
        },
        auth,
      )
      setName('')
      setGrade('')
      setSection('')
      setTeacherId('')
      onAdded()
    } catch (err) {
      setFailed(err instanceof Error && err.message ? err.message : t.classAddFailed)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Panel raised>
      <form
        onSubmit={(e) => {
          e.preventDefault()
          void submit()
        }}
      >
        <div className="flex items-start justify-between gap-3">
          <h2 className="text-dash-md font-extrabold text-ink">{t.addClass}</h2>
          <CloseButton label={t.close} onClose={onClose} />
        </div>
        <p className="mt-1 max-w-prose text-dash-xs text-muted">{t.addClassHint}</p>

        {/*
          ⚠️ TWO COLUMNS IN THE DIALOG, NOT THREE. The grid was `lg:grid-cols-3`
          when it ran the full width of the page; inside a `max-w-2xl` card a
          third column puts three inputs in about as much room as two want, and
          "Section" is a single character next to a class name that is not.
        */}
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <Field label={t.className} htmlFor="class-name" width="full">
            <Input
              id="class-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={80}
              placeholder={t.classNamePlaceholder}
            />
          </Field>
          {/* 🔴 GRADE AND SECTION ARE SEPARATE COLUMNS AND SEPARATE BOXES —
              "Class 2, Section B" is how an Indian school names a class, and a
              single free string cannot be grouped by grade afterwards. Both are
              optional: a single-stream nursery is not made to invent a section. */}
          <Field label={t.grade} htmlFor="class-grade" width="full">
            <Input
              id="class-grade"
              value={grade}
              onChange={(e) => setGrade(e.target.value)}
              maxLength={24}
              placeholder={t.gradePlaceholder}
            />
          </Field>
          <Field label={t.section} htmlFor="class-section" width="full">
            <Input
              id="class-section"
              value={section}
              onChange={(e) => setSection(e.target.value)}
              maxLength={16}
              placeholder={t.sectionPlaceholder}
            />
          </Field>
          <Field label={t.band} htmlFor="class-band" width="full">
            <Select
              id="class-band"
              value={ageBand}
              onChange={(e) => setAgeBand(e.target.value as AgeBand)}
            >
              {AGE_BANDS.map((b) => (
                <option key={b} value={b}>
                  {BAND_LABEL[b][lang]}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t.classTeacher} htmlFor="class-teacher" width="full">
            <Select
              id="class-teacher"
              value={teacherId}
              onChange={(e) => setTeacherId(e.target.value)}
            >
              <option value="">{t.noTeacherYet}</option>
              {teachers.map((row) => (
                <option key={row.userId} value={row.userId}>
                  {row.name ?? row.email ?? row.userId}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <p className="mt-2 max-w-prose text-dash-xs text-muted">{t.bandHint}</p>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <DashButton type="submit" disabled={busy || name.trim().length === 0}>
            {busy ? t.adding : t.addClass}
          </DashButton>
          {/* ⚠️ `type="button"`. Inside a form a bare <button> submits, so a
              "Cancel" that is not typed is a "Save" wearing the wrong label. */}
          <DashButton type="button" variant="ghost" onClick={onClose} disabled={busy}>
            {t.cancel}
          </DashButton>
        </div>

        {failed !== null && (
          <div className="mt-4 max-w-prose">
            <Note tone="warning" icon={<AlertIcon size={16} />}>
              {failed}
            </Note>
          </div>
        )}
      </form>
    </Panel>
  )
}

/* ──────────────────────────────────────────────────── one class ── */

/**
 * 🔴 COLLAPSED BY DEFAULT, AND THE HEADER CARRIES ENOUGH TO DECIDE WITHOUT
 * OPENING IT. A school with twelve classes gets twelve cards; if the only way to
 * see that 2B still has no teacher is to expand 2B, the screen is a filing
 * cabinet. The gaps are on the closed row for exactly that reason.
 */
function ClassCard({
  lang,
  auth,
  canWrite,
  klass,
  subjects,
  teachers,
  onChanged,
}: {
  lang: Lang
  auth?: StaffAuth
  canWrite: boolean
  klass: ClassSummary
  subjects: readonly SubjectRow[]
  teachers: readonly SchoolStaffRow[]
  onChanged: () => void
}) {
  const t = ADMIN_COPY[lang]
  const [open, setOpen] = useState(false)

  /*
   * ⚠️ THE CLASS'S SUBJECTS ARE FETCHED ONLY ONCE IT IS OPENED. Twelve classes
   * would otherwise be twelve requests on first paint for rows nobody has looked
   * at — `useResource` is keyed on the open flag so the call happens on expand.
   */
  const classSubjects = useResource<ClassSubjectRow[]>(
    (signal) => (open ? getClassSubjects(klass.id, { ...auth, signal }) : Promise.resolve([])),
    ['e4-class-subjects', klass.id, String(open)],
  )

  const gaps = classGaps(klass, classSubjects.data ?? [])
  const editable = isEditable(klass)

  return (
    <div className="rounded-xl border border-line bg-surface">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full flex-wrap items-center justify-between gap-3 px-4 py-3 text-left"
      >
        <span className="flex flex-wrap items-center gap-2">
          <span className="text-dash-md font-extrabold text-ink">{klass.name}</span>
          {klass.grade !== null && (
            <span className="text-dash-xs text-muted">
              {klass.grade}
              {klass.section !== null ? ` · ${klass.section}` : ''}
            </span>
          )}
          <StatusPill tone="neutral" icon={<ChildrenIcon size={14} />}>
            {t.childrenCount(klass.childCount)}
          </StatusPill>
          <StatusPill tone="neutral" icon={<InfoIcon size={14} />}>
            {BAND_LABEL[klass.ageBand][lang]}
          </StatusPill>
          {/*
            🔴 THE SIGN-IN CODE, ON THE CLASS ROW. Until now it existed only
            behind the teacher's play-setup screen, so a school admin could see
            everything about a class except the one value a child types — and
            answering "what do they put in the first box" meant asking a teacher
            to open a different dashboard.

            ⚠️ `tabular` AND `select-all`: it is read aloud and copied, and the
            alphabet already excludes I/L/O/S/0/1/5 for the same reason.
          */}
          {klass.code !== null ? (
            <StatusPill tone="neutral" icon={<PointIcon size={14} />}>
              <span className="text-muted">{t.classCodeLabel}</span>{' '}
              <span className="tabular select-all font-extrabold tracking-wider">{klass.code}</span>
            </StatusPill>
          ) : (
            /* ⚠️ WARNING, NOT NEUTRAL. No code means no child in this class can
               sign in at all — the same weight as having no teacher. */
            <StatusPill tone="warning" icon={<AlertIcon size={14} />}>
              {t.classCodeNone}
            </StatusPill>
          )}
          {klass.teacher !== null ? (
            <StatusPill tone="success" icon={<PointIcon size={14} />}>
              {klass.teacher.name ?? t.dash}
            </StatusPill>
          ) : (
            <StatusPill tone="warning" icon={<AlertIcon size={14} />}>
              {t.noTeacherYet}
            </StatusPill>
          )}
          {!editable && (
            <StatusPill tone="neutral" icon={<InfoIcon size={14} />}>
              {t.archived}
            </StatusPill>
          )}
        </span>
        <span className="text-dash-xs font-bold text-muted">{open ? t.close : t.setUp}</span>
      </button>

      {open && (
        <div className="border-t border-line px-4 py-4">
          {gaps.length > 0 && (
            <div className="mb-4 flex max-w-prose flex-col gap-2">
              {gaps.map((gap) => (
                <Note key={gap} tone="warning" icon={<InfoIcon size={16} />}>
                  {gapText(gap, lang)}
                </Note>
              ))}
            </div>
          )}

          {!editable ? (
            /* ⚠️ THE SERVER REFUSES EVERY WRITE ON AN ARCHIVED CLASS, so the
               controls are not drawn — D4 rather than a form that cannot save. */
            <Note tone="neutral" icon={<InfoIcon size={16} />}>
              {t.archivedReadOnly}
            </Note>
          ) : (
            <>
              {canWrite && (
                <ClassTeacherField
                  lang={lang}
                  auth={auth}
                  klass={klass}
                  teachers={teachers}
                  onChanged={onChanged}
                />
              )}

              <div className="mt-5">
                <h3 className="text-dash-sm font-extrabold text-ink">{t.classSubjects}</h3>
                <p className="mt-1 max-w-prose text-dash-xs text-muted">{t.classSubjectsHint}</p>

                <div className="mt-3">
                  <Surface
                    resource={classSubjects}
                    lang={lang}
                    emptyMessage={t.classSubjectsEmpty}
                    skeletonRows={2}
                  >
                    {(rows) =>
                      rows.length === 0 ? (
                        <p className="text-muted">{t.classSubjectsEmpty}</p>
                      ) : (
                        <ul className="flex flex-col gap-2">
                          {rows.map((row) => (
                            <ClassSubjectRowView
                              key={row.id}
                              lang={lang}
                              auth={auth}
                              canWrite={canWrite}
                              klass={klass}
                              row={row}
                              teachers={teachers}
                              onChanged={() => classSubjects.retry()}
                            />
                          ))}
                        </ul>
                      )
                    }
                  </Surface>
                </div>

                {canWrite && (
                  <AddSubjectToClass
                    lang={lang}
                    auth={auth}
                    klass={klass}
                    subjects={subjects}
                    on={classSubjects.data ?? []}
                    teachers={teachers}
                    onAdded={() => classSubjects.retry()}
                  />
                )}
              </div>

              {/*
                🔴 THE OFFICE'S OWN ADD, WITH THE FAMILY'S DETAILS — the half a
                teacher's screen deliberately does not have.

                Until now the only way to create a child was `POST
                /roster/import`, and the only UI for that (`RosterImport`) is
                rendered nowhere in this tier. So an admin who gained ONE child
                in October had to build a one-row spreadsheet, and the register
                drifted from the room while every report in this product counted
                the wrong set of children.

                ⚠️ THE PARENT FIELDS ARE WHAT MAKES THIS THE ADMIN'S FORM RATHER
                THAN A COPY OF THE TEACHER'S. `withParent` draws them; the server
                independently demands `write` on the class the moment a phone
                number is in the body, which only an admin has. The flag chooses
                what to DRAW, never what is allowed.
              */}
              {canWrite && (
                <div className="mt-5 border-t border-line pt-5">
                  <h3 className="text-dash-sm font-extrabold text-ink">{t.addChildTitle}</h3>
                  <p className="mt-1 max-w-prose text-dash-xs text-muted">{t.addChildHint}</p>
                  <div className="mt-3">
                    <AddStudentForm
                      classId={klass.id}
                      lang={lang}
                      auth={auth}
                      withParent
                      onAdded={onChanged}
                    />
                  </div>
                </div>
              )}

              {canWrite && (
                <ArchiveClass lang={lang} auth={auth} klass={klass} onArchived={onChanged} />
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}

/** The person responsible for the class — whose name is on the report. */
function ClassTeacherField({
  lang,
  auth,
  klass,
  teachers,
  onChanged,
}: {
  lang: Lang
  auth?: StaffAuth
  klass: ClassSummary
  teachers: readonly SchoolStaffRow[]
  onChanged: () => void
}) {
  const t = ADMIN_COPY[lang]
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState<string | null>(null)

  const assign = async (value: string) => {
    setBusy(true)
    setFailed(null)
    try {
      /* 🔴 `null`, NOT `undefined`. `updateClassSchema` treats absent and null
         differently: `{}` changes nothing and `teacherId: null` means unassign.
         Sending undefined would silently become "leave it alone". */
      await updateClass(klass.id, { teacherId: value === '' ? null : value }, auth)
      onChanged()
    } catch (err) {
      setFailed(err instanceof Error && err.message ? err.message : t.assignFailed)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="max-w-sm">
      <Field label={t.classTeacher} htmlFor={`teacher-${klass.id}`} width="full">
        <Select
          id={`teacher-${klass.id}`}
          value={klass.teacher?.id ?? ''}
          disabled={busy}
          onChange={(e) => void assign(e.target.value)}
        >
          <option value="">{t.noTeacherYet}</option>
          {teachers.map((row) => (
            <option key={row.userId} value={row.userId}>
              {row.name ?? row.email ?? row.userId}
            </option>
          ))}
        </Select>
      </Field>
      <p className="mt-1 max-w-prose text-dash-xs text-muted">{t.classTeacherHint}</p>
      {failed !== null && (
        <div className="mt-2">
          <Note tone="warning" icon={<AlertIcon size={16} />}>
            {failed}
          </Note>
        </div>
      )}
    </div>
  )
}

/** One subject on this class, and who takes it. */
function ClassSubjectRowView({
  lang,
  auth,
  canWrite,
  klass,
  row,
  teachers,
  onChanged,
}: {
  lang: Lang
  auth?: StaffAuth
  canWrite: boolean
  klass: ClassSummary
  row: ClassSubjectRow
  teachers: readonly SchoolStaffRow[]
  onChanged: () => void
}) {
  const t = ADMIN_COPY[lang]
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState<string | null>(null)

  const takenBy = subjectTeacher(row, klass.teacher)

  const change = async (value: string) => {
    setBusy(true)
    setFailed(null)
    try {
      await setSubjectTeacher(klass.id, row.id, value === '' ? null : value, auth)
      onChanged()
    } catch (err) {
      setFailed(err instanceof Error && err.message ? err.message : t.assignFailed)
    } finally {
      setBusy(false)
    }
  }

  const remove = async () => {
    setBusy(true)
    setFailed(null)
    try {
      await removeClassSubject(klass.id, row.id, auth)
      onChanged()
    } catch (err) {
      setFailed(err instanceof Error && err.message ? err.message : t.subjectRemoveFailed)
    } finally {
      setBusy(false)
    }
  }

  return (
    <li className="flex flex-wrap items-center gap-3 rounded-lg border border-line bg-canvas px-3 py-2">
      <span className="min-w-[7rem] font-bold text-ink">{row.name}</span>

      {canWrite ? (
        <span className="flex items-center gap-2">
          <Select
            aria-label={t.subjectTeacherLabel(row.name)}
            value={row.teacher?.id ?? ''}
            disabled={busy}
            onChange={(e) => void change(e.target.value)}
          >
            {/* 🔴 THE EMPTY OPTION IS NOT "NOBODY" — it is "the class teacher
                takes it", which is the ordinary arrangement and the reason the
                column is nullable. Labelling it "None" would make an admin
                believe they had left a lesson uncovered. */}
            <option value="">{t.theClassTeacher}</option>
            {teachers.map((row2) => (
              <option key={row2.userId} value={row2.userId}>
                {row2.name ?? row2.email ?? row2.userId}
              </option>
            ))}
          </Select>
          <button
            type="button"
            disabled={busy}
            onClick={() => void remove()}
            className="text-dash-xs font-bold text-muted underline"
            aria-label={t.subjectRemoveFromClassLabel(row.name)}
          >
            {t.remove}
          </button>
        </span>
      ) : takenBy === null ? (
        <StatusPill tone="warning" icon={<AlertIcon size={14} />}>
          {t.nobodyTakesIt}
        </StatusPill>
      ) : (
        <StatusPill tone="neutral" icon={<PointIcon size={14} />}>
          {takenBy.inherited
            ? t.takenByClassTeacher(takenBy.name ?? t.dash)
            : (takenBy.name ?? t.dash)}
        </StatusPill>
      )}

      {/* ⚠️ SHOWN EVEN WHEN THE DROPDOWN IS, because the dropdown's empty option
          says "the class teacher" without saying WHO that is. */}
      {canWrite && takenBy !== null && takenBy.inherited && (
        <span className="text-dash-xs text-muted">
          {t.takenByClassTeacher(takenBy.name ?? t.dash)}
        </span>
      )}
      {canWrite && takenBy === null && (
        <span className="text-dash-xs font-bold" style={{ color: 'var(--color-pill-warning-fg)' }}>
          {t.nobodyTakesIt}
        </span>
      )}

      {failed !== null && (
        <span className="w-full">
          <Note tone="warning" icon={<AlertIcon size={16} />}>
            {failed}
          </Note>
        </span>
      )}
    </li>
  )
}

function AddSubjectToClass({
  lang,
  auth,
  klass,
  subjects,
  on,
  teachers,
  onAdded,
}: {
  lang: Lang
  auth?: StaffAuth
  klass: ClassSummary
  subjects: readonly SubjectRow[]
  on: readonly ClassSubjectRow[]
  teachers: readonly SchoolStaffRow[]
  onAdded: () => void
}) {
  const t = ADMIN_COPY[lang]
  const [subjectId, setSubjectId] = useState('')
  const [teacherId, setTeacherId] = useState('')
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState<string | null>(null)

  const available = addableSubjects(subjects, on)

  const add = async () => {
    setBusy(true)
    setFailed(null)
    try {
      await addClassSubject(
        klass.id,
        { subjectId, teacherId: teacherId === '' ? null : teacherId },
        auth,
      )
      setSubjectId('')
      setTeacherId('')
      onAdded()
    } catch (err) {
      setFailed(err instanceof Error && err.message ? err.message : t.subjectAddFailed)
    } finally {
      setBusy(false)
    }
  }

  /*
   * 🔴 A SENTENCE, NOT AN EMPTY DROPDOWN. Two different states land here — the
   * school has named no subjects at all, and this class already studies all of
   * them — and they need different sentences, because the first is fixed at the
   * top of this screen and the second is not a problem.
   */
  if (subjects.length === 0) {
    return <p className="mt-3 text-dash-xs text-muted">{t.noSubjectsNamedYet}</p>
  }
  if (available.length === 0) {
    return <p className="mt-3 text-dash-xs text-muted">{t.everySubjectAlreadyOn}</p>
  }

  return (
    <form
      className="mt-3 flex flex-wrap items-end gap-3"
      onSubmit={(e) => {
        e.preventDefault()
        void add()
      }}
    >
      <Field label={t.addSubject} htmlFor={`add-subject-${klass.id}`}>
        <Select
          id={`add-subject-${klass.id}`}
          value={subjectId}
          onChange={(e) => setSubjectId(e.target.value)}
        >
          <option value="">{t.chooseSubject}</option>
          {available.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>
      </Field>
      <Field label={t.takenBy} htmlFor={`add-subject-teacher-${klass.id}`}>
        <Select
          id={`add-subject-teacher-${klass.id}`}
          value={teacherId}
          onChange={(e) => setTeacherId(e.target.value)}
        >
          <option value="">{t.theClassTeacher}</option>
          {teachers.map((row) => (
            <option key={row.userId} value={row.userId}>
              {row.name ?? row.email ?? row.userId}
            </option>
          ))}
        </Select>
      </Field>
      <DashButton type="submit" disabled={busy || subjectId === ''}>
        {t.subjectAdd}
      </DashButton>

      {failed !== null && (
        <div className="w-full max-w-prose">
          <Note tone="warning" icon={<AlertIcon size={16} />}>
            {failed}
          </Note>
        </div>
      )}
    </form>
  )
}

/**
 * 🔴 IT ARCHIVES, AND THE CONFIRMATION SAYS WHAT THAT MEANS BEFORE THE CLICK.
 * `Player.classId` is `SetNull`, so a hard delete would leave every child in the
 * class with no class — rows intact and invisible to every scoped query,
 * including the admin who ran it. Archiving is the only safe removal, and an
 * admin who believes "delete" wipes the children will never press it while one
 * who believes archiving is reversible-by-a-click will press it once.
 */
function ArchiveClass({
  lang,
  auth,
  klass,
  onArchived,
}: {
  lang: Lang
  auth?: StaffAuth
  klass: ClassSummary
  onArchived: () => void
}) {
  const t = ADMIN_COPY[lang]
  const [confirming, setConfirming] = useState(false)
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState<string | null>(null)

  const archive = async () => {
    setBusy(true)
    setFailed(null)
    try {
      await archiveClass(klass.id, auth)
      setConfirming(false)
      onArchived()
    } catch (err) {
      setFailed(err instanceof Error && err.message ? err.message : t.archiveFailed)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="mt-6 border-t border-line pt-4">
      {!confirming ? (
        <DashButton variant="ghost" onClick={() => setConfirming(true)}>
          {t.archiveClass}
        </DashButton>
      ) : (
        <div
          className="max-w-prose rounded-lg p-4"
          style={{
            background: 'var(--color-pill-danger-bg)',
            border: '1px solid var(--color-pill-danger-line)',
          }}
          role="alertdialog"
          aria-label={t.archiveConfirm(klass.name)}
        >
          <p className="font-bold" style={{ color: 'var(--color-pill-danger-fg)' }}>
            {t.archiveConfirm(klass.name)}
          </p>
          <p className="mt-1 text-dash-xs" style={{ color: 'var(--color-pill-danger-fg)' }}>
            {t.archiveMeaning(klass.childCount)}
          </p>
          <div className="mt-4 flex gap-2">
            <DashButton variant="destructive" disabled={busy} onClick={() => void archive()}>
              {busy ? t.archiving : t.archiveConfirmYes}
            </DashButton>
            <DashButton variant="ghost" onClick={() => setConfirming(false)}>
              {t.cancel}
            </DashButton>
          </div>
        </div>
      )}

      {failed !== null && (
        <div className="mt-3 max-w-prose">
          <Note tone="warning" icon={<AlertIcon size={16} />}>
            {failed}
          </Note>
        </div>
      )}
    </div>
  )
}
